import { useCallback, useEffect } from 'react';
import { Button, Stack, Text } from '@adc/parallax-component-library';
import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';

import { useUserActivationData } from '~/api/query/userActivationData';
import { useUserActivationUpdate } from '~/api/query/userActivationUpdate';
import { CLMEmailInput } from '~/components/inputs/CLMEmailInput';
import { CLMPasswordInput } from '~/components/inputs/CLMPasswordInput';
import { CLMPhoneInput } from '~/components/inputs/CLMPhoneNumberInput';
import { CLMTextInput } from '~/components/inputs/CLMTextInput';
import { PasswordSettings } from '~/constants/forms';
import { userAccountInformationSchema } from '~/features/activation/validations';
import { decodeJwt } from '~/features/auth/jwt';
import { USER_ROLE_NAME, type UserRoleValue } from '~/features/auth/roles';
import { authState } from '~/features/auth/state';
import { useModal } from '~/features/modals/useModal';
import { usePasswordValidation } from '~/hooks/usePassword';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';
import { publicRoute } from '~/utils/routesAuthorization';

const testID = 'activation-flow-account';

export const Route = createFileRoute('/activation/$token/$userID/account')({
  component: RouteComponent,
  beforeLoad: publicRoute,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { token, userID } = Route.useParams();

  const { data, isLoading } = useUserActivationData({
    token,
    userID,
  });

  const userActivation = useUserActivationUpdate();

  const { openModal } = useModal();

  const passwordSchema = usePasswordValidation({
    hasNewPassword: true,
    validateCurrentPassword: false,
    user: {
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      email: data?.email ?? '',
    },
    translations: {
      password: PasswordSettings,
    },
  });

  const handleBack = useCallback(() => {
    void navigate({ to: `/activation/${token}/legal`, replace: true });
  }, [navigate, token]);

  const form = useForm({
    defaultValues: {
      id: userID,
      email: data?.email ?? '',
      phoneNumber: data?.phoneNumber ?? '',
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      password: '',
      repeatPassword: '',
    },
    validators: {
      onChange: userAccountInformationSchema.and(passwordSchema),
    },
    onSubmit: async ({ value }) => {
      const { id, phoneNumber, password } = value;
      const response = await userActivation.mutateAsync({
        token,
        id,
        phoneNumber,
        privacyNoticeVersion: 'v1.0.0',
        termsOfUseVersion: 'v1.0.0',
        password,
      });

      if (!response.accessToken) {
        throw new Error('Failed to update user activation data');
      }

      const decodedJwt = decodeJwt(response.accessToken);

      const userData = response.include.user;

      authState.set({
        accessToken: response.accessToken,
        tokenType: response.tokenType,
        decodedJWTPayloard: decodedJwt,
        userInfo: {
          userID: decodedJwt.userID,
          ...userData,
        },
      });

      await navigate({ to: '/', replace: true });
      openModal('AccountActivated', { testID });
    },
  });

  useEffect(() => {
    // If no data for the form, invalid token
    if (!isLoading && data?.status !== 'inactive') {
      void navigate({ to: '/', replace: true });
    }
  }, [isLoading, navigate, data]);

  return (
    !isLoading && (
      <Stack
        borderWidth="$0"
        maxWidth={800}
        width="100%"
        borderRadius="$1.5"
        backgroundColor="$surface.container"
        padding="$6"
        marginHorizontal="auto"
        marginTop="$9"
        gap="$6"
      >
        <Text type="$h3.medium">Account information</Text>
        <Text
          type="$bodyBase.default"
          testID={buildTestId({
            testID,
            suffix: ['user', 'role'],
          })}
        >
          You’ve been invited to join the LactateView team as{' '}
          <span>
            {data?.role && USER_ROLE_NAME[data.role as UserRoleValue]}
          </span>
          .
        </Text>
        <Stack flexDirection="column" gap="$6">
          <Stack
            flexDirection="row"
            gap="$6"
            $xs={{
              flexDirection: 'column',
            }}
          >
            <Stack flex={1} flexShrink={1} flexBasis="0%">
              <form.Field name="firstName">
                {(field) => (
                  <CLMTextInput
                    testID={buildTestId({
                      testID,
                      suffix: ['input', 'first', 'name'],
                    })}
                    disabled
                    label="First Name"
                    placeholder="First Name"
                    value={field.state.value}
                    onChangeText={field.handleChange}
                    onBlur={field.handleBlur}
                    autoCapitalize="words"
                    {...getErrorProps(field)}
                  />
                )}
              </form.Field>
            </Stack>
            <Stack flex={1} flexShrink={1} flexBasis="0%">
              <form.Field name="lastName">
                {(field) => (
                  <CLMTextInput
                    testID={buildTestId({
                      testID,
                      suffix: ['input', 'last', 'name'],
                    })}
                    label="Last Name"
                    disabled
                    placeholder="Last Name"
                    value={field.state.value}
                    onChangeText={field.handleChange}
                    onBlur={field.handleBlur}
                    autoCapitalize="words"
                    {...getErrorProps(field)}
                  />
                )}
              </form.Field>
            </Stack>
          </Stack>

          <Stack flex={1} flexShrink={1} flexBasis="0%">
            <form.Field name="phoneNumber">
              {(field) => (
                <CLMPhoneInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'phone', 'number'],
                  })}
                  label="Phone Number (optional)"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Text type="$h4.medium" color="$color.gray11Dark">
            Login information
          </Text>
          <Stack flex={1} flexShrink={1} flexBasis="0%">
            <form.Field name="email">
              {(field) => (
                <CLMEmailInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'email'],
                  })}
                  label="Email"
                  placeholder="Email"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  disabled
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack
            flexDirection="row"
            gap="$6"
            $xs={{
              flexDirection: 'column',
            }}
          >
            <Stack flex={1} flexBasis="50%">
              <form.Field name="password">
                {(field) => {
                  const isValid = field.state.meta.isValid ? 'yes' : 'no';

                  return (
                    <CLMPasswordInput
                      valid={field.state.meta.isTouched ? isValid : 'pending'}
                      onChangeText={field.handleChange}
                      onBlur={field.handleBlur}
                      autoComplete="new-password"
                      {...getErrorProps(field, PasswordSettings.minLength())}
                      testID={buildTestId({
                        testID,
                        suffix: 'password',
                      })}
                    />
                  );
                }}
              </form.Field>
            </Stack>
            <Stack flex={1} flexBasis="50%">
              <form.Field name="repeatPassword">
                {(field) => {
                  const arePasswordsMatching = field.state.meta.isValid
                    ? 'yes'
                    : 'no';

                  return (
                    <CLMPasswordInput
                      label="Repeat Password"
                      onChangeText={field.handleChange}
                      onBlur={field.handleBlur}
                      autoComplete="new-password"
                      valid={
                        field.state.meta.isTouched
                          ? arePasswordsMatching
                          : 'pending'
                      }
                      alwaysShowSupportingRow
                      {...getErrorProps(field, PasswordSettings.mustMatch)}
                      testID={buildTestId({
                        testID,
                        suffix: ['repeat', 'password'],
                      })}
                    />
                  );
                }}
              </form.Field>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          marginTop="$4"
          flexDirection="row"
          gap="$4"
          justifyContent="flex-end"
        >
          <Button
            // @ts-expect-error Parallax doesn't type Button minWidth
            minWidth={164}
            label="Back"
            type="button"
            variant="secondary"
            onPress={handleBack}
            testID={buildTestId({
              testID,
              suffix: 'back',
            })}
          />
          <form.Subscribe selector={(state) => [state.isSubmitting]}>
            {([isSubmitting]) => (
              <Button
                type="submit"
                variant="primary"
                // @ts-expect-error Parallax doesn't type Button minWidth
                minWidth={164}
                label={isSubmitting ? 'Sending...' : 'Complete'}
                disabled={isSubmitting}
                onPress={() => {
                  void form.handleSubmit();
                }}
                testID={buildTestId({
                  testID,
                  suffix: 'submit',
                })}
              />
            )}
          </form.Subscribe>
        </Stack>
      </Stack>
    )
  );
}
